// =============================================================================
// SubbieOnboardingScreen — /onboard/subbie  (T-154)
//
// The subbie self-onboarding wizard. Reached automatically by users
// invited with login_type='subcontractor' on first login (see
// OnboardingGate.tsx).
//
// Flow:
//   Step 1 — Pick your track: Labour or Supplier
//   Step 2 — Person details: name, DOB, mobile, email   (always)
//   Step 3 — Pick your company                          (Supplier only)
//   Step 4 — Upload insurance certificates              (Supplier only, skippable)
//   Step 5 — Upload your licences/tickets               (Supplier only, skippable)
//   Submit → app.complete_subbie_onboarding RPC
//
// Labour subbies have a 2-step wizard. Supplier subbies have a 5-step
// wizard with two skippable upload steps (they can come back and do
// these from their portal).
//
// On submit the RPC stamps onboarding_completed_at + flips role/login_type
// to the correct post-onboarding state:
//   Labour   → role=field,         login_type=employee
//   Supplier → role=subcontractor, login_type=subcontractor
// =============================================================================

import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  HardHat, Building2, ChevronLeft, ChevronRight, AlertTriangle,
  IdCard, ShieldCheck, Check, LogOut,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/lib/auth";
import { Button, Card, Spinner, Field, Input } from "@/components/ui";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

type Track = "labour" | "supplier";

interface SubbieDraft {
  track:        Track | null;
  // Step 2 — person
  first_name:   string;
  last_name:    string;
  date_of_birth: string;     // ISO yyyy-mm-dd
  email:        string;
  mobile:       string;
  // Step 3 — supplier branch only
  subcontractor_company_id: string | null;
}

interface SubcontractorCompanyOption {
  subcontractor_company_id: string;
  legal_name:               string;
  trading_name:             string | null;
  abn:                      string | null;
  primary_contact_name:     string | null;
  is_approved:              boolean;
  public_liability_expiry:  string | null;
  workers_comp_expiry:      string | null;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export default function SubbieOnboardingScreen() {
  const user    = useAuthStore(s => s.user);
  const signOut = useAuthStore(s => s.signOut);

  const [step,  setStep]  = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<SubbieDraft>({
    track:                    null,
    first_name:               user?.first_name ?? "",
    last_name:                user?.last_name  ?? "",
    date_of_birth:            "",
    email:                    user?.login_email ?? "",
    mobile:                   "",
    subcontractor_company_id: null,
  });

  const update = <K extends keyof SubbieDraft>(k: K, v: SubbieDraft[K]) =>
    setDraft(prev => ({ ...prev, [k]: v }));

  // Total step count depends on the track. Labour = 2, Supplier = 5.
  const totalSteps = draft.track === "supplier" ? 5 : draft.track === "labour" ? 2 : 1;

  // ---------------------------------------------------------------------------
  // Step 3 — fetch active subcontractor companies for the picker
  // ---------------------------------------------------------------------------
  const { data: companies, isLoading: loadingCompanies } = useQuery({
    queryKey: ["subbie_active_companies"],
    enabled: draft.track === "supplier" && step >= 3,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_active_subcontractor_companies")
        .select(`
          subcontractor_company_id, legal_name, trading_name, abn,
          primary_contact_name, is_approved,
          public_liability_expiry, workers_comp_expiry
        `);
      if (error) throw error;
      return (data ?? []) as SubcontractorCompanyOption[];
    },
  });

  // ---------------------------------------------------------------------------
  // Submit — call complete_subbie_onboarding RPC
  // ---------------------------------------------------------------------------
  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!user)        throw new Error("Not signed in");
      if (!draft.track) throw new Error("Pick a track first");

      const payload: Record<string, unknown> = {
        subbie_track:   draft.track,
        first_name:     draft.first_name.trim(),
        last_name:      draft.last_name.trim(),
        date_of_birth:  draft.date_of_birth || null,
        email:          draft.email.trim(),
        mobile:         draft.mobile.trim(),
      };
      if (draft.track === "supplier") {
        payload.subcontractor_company_id = draft.subcontractor_company_id;
      }

      const { data, error } = await supabase
        .schema("app")
        .rpc("complete_subbie_onboarding", {
          p_target_user_id: user.auth_user_id,
          p_payload:        payload,
        });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // The auth store will reload on next session refresh, but we want
      // an immediate role/login_type bump. Cleanest: reload the page so
      // the OnboardingGate re-evaluates with fresh data.
      window.location.replace("/");
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message
                 : (err as { message?: string })?.message ?? "Submit failed";
      setError(msg);
    },
  });

  // ---------------------------------------------------------------------------
  // Step validation
  // ---------------------------------------------------------------------------
  const canAdvance = useMemo(() => {
    if (step === 1) return !!draft.track;
    if (step === 2) {
      return !!draft.first_name.trim()
          && !!draft.last_name.trim()
          && !!draft.date_of_birth
          && !!draft.email.trim()
          && !!draft.mobile.trim();
    }
    if (step === 3) return !!draft.subcontractor_company_id;
    // Steps 4/5 are skippable
    return true;
  }, [step, draft]);

  const next = () => {
    setError(null);
    if (step < totalSteps) setStep(step + 1);
    else submitMutation.mutate();
  };
  const back = () => {
    setError(null);
    if (step > 1) setStep(step - 1);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (!user) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Spinner size={20} />
      </div>
    );
  }

  const progressPct = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-2xl mx-auto p-4 md:p-6 pb-24">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">
                Subcontractor onboarding
              </div>
              <h1 className="text-xl font-semibold mt-1">
                Welcome to Coretel
                {user.first_name && <>, {user.first_name}</>}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-[11px] text-text-muted font-mono">
                Step {step} / {totalSteps}
              </div>
              <button
                onClick={() => { void signOut(); }}
                className="text-[11px] text-text-muted hover:text-text inline-flex items-center gap-1"
                title="Sign out"
              >
                <LogOut size={11} /> Sign out
              </button>
            </div>
          </div>
          <div className="h-1 bg-line rounded-full overflow-hidden">
            <div
              className="h-full bg-accent transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Step body */}
        {step === 1 && (
          <Step1Track
            value={draft.track}
            onChange={t => update("track", t)}
          />
        )}
        {step === 2 && (
          <Step2Person
            draft={draft}
            update={update}
          />
        )}
        {step === 3 && draft.track === "supplier" && (
          <Step3Company
            companies={companies ?? []}
            loading={loadingCompanies}
            value={draft.subcontractor_company_id}
            onChange={id => update("subcontractor_company_id", id)}
          />
        )}
        {step === 4 && draft.track === "supplier" && <Step4Insurance />}
        {step === 5 && draft.track === "supplier" && <Step5Licences />}

        {/* Error */}
        {error && (
          <Card className="mt-4 border-danger/30">
            <div className="p-4 flex items-start gap-3 text-sm text-danger">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5"/>
              <span>{error}</span>
            </div>
          </Card>
        )}

        {/* Footer nav */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            icon={ChevronLeft}
            onClick={back}
            disabled={step === 1 || submitMutation.isPending}
          >
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              icon={step === totalSteps ? Check : ChevronRight}
              onClick={next}
              disabled={!canAdvance || submitMutation.isPending}
            >
              {step === totalSteps
                ? (submitMutation.isPending ? "Submitting…" : "Finish & enter app")
                : "Continue"}
            </Button>
          </div>
        </div>

        {/* Skip note for supplier upload steps */}
        {draft.track === "supplier" && (step === 4 || step === 5) && (
          <div className="mt-3 text-[11px] text-text-muted text-center italic">
            You can skip this and upload from your profile later.
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Step 1 — Track picker
// =============================================================================

function Step1Track({
  value, onChange,
}: { value: Track | null; onChange: (t: Track) => void }) {
  return (
    <div>
      <h2 className="text-[15px] font-semibold mb-1">How do you work with Coretel?</h2>
      <p className="text-[13px] text-text-muted mb-4">
        Pick the option that matches your engagement. Not sure? Ask the
        Coretel manager who invited you.
      </p>
      <div className="grid gap-3">
        <TrackCard
          icon={HardHat}
          title="Labour"
          subtitle="Sole-trader / ABN day rate"
          description="You work alongside the Coretel crew under our supervision. Day rate, our gear, our trucks. We'll handle your scheduling and HSE inductions like an employee."
          selected={value === "labour"}
          onClick={() => onChange("labour")}
        />
        <TrackCard
          icon={Building2}
          title="Supplier"
          subtitle="Subcontractor company"
          description="You represent a sub-contracting company we engage to do scoped work — your own crew, your own gear, your own pricing. You'll need your insurance certificates handy."
          selected={value === "supplier"}
          onClick={() => onChange("supplier")}
        />
      </div>
    </div>
  );
}

interface TrackCardProps {
  icon:        typeof HardHat;
  title:       string;
  subtitle:    string;
  description: string;
  selected:    boolean;
  onClick:     () => void;
}

function TrackCard({ icon: Icon, title, subtitle, description, selected, onClick }: TrackCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-lg border p-4 transition-all ${
        selected
          ? "border-accent bg-accent/5 ring-2 ring-accent/40"
          : "border-line bg-ink-raised hover:border-accent/50"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded ${selected ? "bg-accent/20 text-accent" : "bg-ink text-text-muted"}`}>
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[14px]">{title}</div>
            {selected && <Check size={16} className="text-accent"/>}
          </div>
          <div className="text-[11px] font-mono uppercase tracking-wider2 text-text-muted mt-0.5">
            {subtitle}
          </div>
          <p className="text-[12px] text-text-muted mt-2 leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </button>
  );
}

// =============================================================================
// Step 2 — Person details
// =============================================================================

function Step2Person({
  draft, update,
}: {
  draft:  SubbieDraft;
  update: <K extends keyof SubbieDraft>(k: K, v: SubbieDraft[K]) => void;
}) {
  return (
    <div>
      <h2 className="text-[15px] font-semibold mb-1">Your details</h2>
      <p className="text-[13px] text-text-muted mb-4">
        We use these to set up your profile and link your records.
      </p>
      <Card>
        <div className="p-4 grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name *">
              <Input
                type="text"
                value={draft.first_name}
                onChange={e => update("first_name", e.target.value)}
                autoComplete="given-name"
              />
            </Field>
            <Field label="Last name *">
              <Input
                type="text"
                value={draft.last_name}
                onChange={e => update("last_name", e.target.value)}
                autoComplete="family-name"
              />
            </Field>
          </div>
          <Field label="Date of birth *">
            <Input
              type="date"
              value={draft.date_of_birth}
              onChange={e => update("date_of_birth", e.target.value)}
              autoComplete="bday"
            />
          </Field>
          <Field label="Email *" hint="Where Coretel sends notifications">
            <Input
              type="email"
              value={draft.email}
              onChange={e => update("email", e.target.value)}
              autoComplete="email"
            />
          </Field>
          <Field label="Mobile *">
            <Input
              type="tel"
              value={draft.mobile}
              onChange={e => update("mobile", e.target.value)}
              placeholder="0400 000 000"
              autoComplete="tel"
            />
          </Field>
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
// Step 3 — Supplier company picker
// =============================================================================

function Step3Company({
  companies, loading, value, onChange,
}: {
  companies: SubcontractorCompanyOption[];
  loading:   boolean;
  value:     string | null;
  onChange:  (id: string) => void;
}) {
  const [filter, setFilter] = useState("");
  const filtered = companies.filter(c => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (c.legal_name?.toLowerCase().includes(q))
        || (c.trading_name?.toLowerCase().includes(q))
        || (c.abn ?? "").includes(filter);
  });

  return (
    <div>
      <h2 className="text-[15px] font-semibold mb-1">Which company do you represent?</h2>
      <p className="text-[13px] text-text-muted mb-4">
        Pick from Coretel's approved subcontractor list. If your company isn't
        here, ask Coretel to add it before you continue.
      </p>
      <div className="mb-3">
        <Input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Search company name or ABN…"
        />
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size={18} />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="p-4 text-[13px] text-text-muted text-center">
            No companies match that search.
          </div>
        </Card>
      ) : (
        <div className="grid gap-2">
          {filtered.map(c => {
            const selected = value === c.subcontractor_company_id;
            const display = c.trading_name ?? c.legal_name;
            return (
              <button
                key={c.subcontractor_company_id}
                type="button"
                onClick={() => onChange(c.subcontractor_company_id)}
                className={`text-left rounded-lg border p-3 transition-all ${
                  selected
                    ? "border-accent bg-accent/5 ring-2 ring-accent/40"
                    : "border-line bg-ink-raised hover:border-accent/50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[14px]">{display}</div>
                    {c.legal_name !== display && (
                      <div className="text-[11px] text-text-muted">{c.legal_name}</div>
                    )}
                    <div className="text-[11px] text-text-muted font-mono mt-0.5">
                      {c.abn ? `ABN ${c.abn}` : "ABN not on file"}
                    </div>
                  </div>
                  {selected && <Check size={16} className="text-accent flex-shrink-0"/>}
                </div>
                {!c.is_approved && (
                  <div className="mt-2 text-[10px] inline-flex items-center gap-1 text-warn">
                    <AlertTriangle size={10}/> Pending Coretel approval
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Step 4 — Insurance certs (placeholder, skippable)
// =============================================================================

function Step4Insurance() {
  return (
    <div>
      <h2 className="text-[15px] font-semibold mb-1">Insurance certificates</h2>
      <p className="text-[13px] text-text-muted mb-4">
        Upload your Public Liability, Workers Comp and (if applicable)
        Professional Indemnity certificates. You can skip this and upload
        from your profile any time — but your Coretel manager will need
        these on file before you start work.
      </p>
      <Card>
        <div className="p-6 text-center">
          <ShieldCheck size={28} className="mx-auto text-text-muted mb-2"/>
          <div className="text-[13px] font-medium mb-1">Coming in your portal</div>
          <p className="text-[12px] text-text-muted">
            Insurance upload is wired to your subbie profile after onboarding.
            Skip for now and upload as soon as you're in.
          </p>
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
// Step 5 — Licences (placeholder, skippable)
// =============================================================================

function Step5Licences() {
  return (
    <div>
      <h2 className="text-[15px] font-semibold mb-1">Your licences & tickets</h2>
      <p className="text-[13px] text-text-muted mb-4">
        HRWL tickets, First Aid, Working at Heights, Confined Space — anything
        you'll need on a Coretel site. Snap a photo of each and Coretel's
        smart-capture will fill in the details. Skip if you'd rather load
        them from your portal later.
      </p>
      <Card>
        <div className="p-6 text-center">
          <IdCard size={28} className="mx-auto text-text-muted mb-2"/>
          <div className="text-[13px] font-medium mb-1">Licence loader available in your portal</div>
          <p className="text-[12px] text-text-muted">
            After you finish onboarding you'll find the licence smart-capture
            on your profile. It's the same flow we use internally —
            photo → AI fill → save.
          </p>
        </div>
      </Card>
    </div>
  );
}

// (Field/Input come from @/components/ui — see Step1_Identity for the
// canonical onboarding pattern.)
